import type { HttpErrorResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AppConfigLoader } from '../../config/app-config.loader';
import { AuthenticationService } from './authentication.service';
import { RegistrationError } from './device-registration-error';

import { LOG_DEBUG } from '../config/debug.config';

import type {
  CompleteAssignmentRequest,
  DeviceChallengeResponse,
  DeviceRegistrationSuccessResponse,
} from '@hunting-nature/shared-core/interfaces/device-registration.interface';

/**
 * Service for handling device registration API calls.
 *
 * This service manages the two-step challenge-response registration flow:
 * 1. Initiate: Backend generates a signed challenge (nonce + backend signature)
 * 2. Complete: Device counter-signs the challenge, app submits proof to backend
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly appConfigLoader = inject(AppConfigLoader);

  private get backend() {
    return this.appConfigLoader.activeConfig().backend;
  }

  /**
   * Initiates the device registration flow by requesting a signed challenge from the backend.
   *
   * The backend generates:
   * - A 256-bit random nonce (32 bytes)
   * - An RSA-4096/SHA-512 signature over the nonce using the backend's private key (512 bytes)
   *
   * @param serialNumber Device serial number (e.g., "DEV-2025-00042")
   * @param modelName Device model name (e.g., "Soluna DS-N4K 4.5-36x40")
   * @returns Challenge response containing challengeId, nonce, and backendSignature
   */
  public initiateAssignment$(serialNumber: string, modelName: string): Observable<DeviceChallengeResponse> {
    const url = `${this.backend.deviceApiUrl}/devices/assign/initiate`;

    if (LOG_DEBUG) console.log('[DeviceApiService] initiateAssignment request:', { url, serialNumber, modelName });

    return this.httpClient
      .post<DeviceChallengeResponse>(
        url,
        {},
        {
          params: {
            serialNumber,
            modelName,
          },
        }
      )
      .pipe(
        map(response => {
          if (LOG_DEBUG) console.log('[DeviceApiService] initiateAssignment succeeded:', { challengeId: response.challengeId });
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('[DeviceApiService] initiateAssignment failed:', { status: error.status, body: error.error });
          throw this.mapErrorResponse(error);
        })
      );
  }

  /**
   * Completes the device registration by submitting the device's counter-signature.
   *
   * The backend verifies:
   * 1. The challenge is valid and not expired
   * 2. The device signature was created using the correct device private key
   * 3. The device is not already assigned to another account
   *
   * @param hardwareId Device hardware identifier (e.g., "Soluna_DS-N4K_4.5-36x40_DEV-2025-00042")
   * @param payload Request containing challengeId and deviceSignature
   * @returns Success response with the registered hardwareId
   */
  public completeAssignment$(hardwareId: string, payload: CompleteAssignmentRequest): Observable<DeviceRegistrationSuccessResponse> {
    const url = `${this.backend.deviceApiUrl}/devices/${encodeURIComponent(hardwareId)}/assign/complete`;

    if (LOG_DEBUG) console.log('[DeviceApiService] completeAssignment request:', { url, hardwareId, challengeId: payload.challengeId });

    return this.httpClient.post<DeviceRegistrationSuccessResponse>(url, payload).pipe(
      map(response => {
        if (LOG_DEBUG) console.log('[DeviceApiService] completeAssignment succeeded:', response);
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('[DeviceApiService] completeAssignment failed:', { status: error.status, body: error.error });
        const registrationError = this.mapErrorResponse(error);
        const retryAfterMs = registrationError === RegistrationError.RATE_LIMITED ? this.resolveRateLimitDurationMs(error) : undefined;
        throw { error: registrationError, retryAfterMs };
      })
    );
  }

  /**
   * Unassigns a device from the current customer account.
   *
   * After successful unassignment, the device is no longer associated with the customer
   * and can be assigned to a different account.
   *
   * @param hardwareId Device hardware identifier (e.g., "Soluna_DS-N4K_4.5-36x40_DEV-2025-00042")
   * @returns Void observable (HTTP 204 No Content on success)
   */
  public unassignDevice$(hardwareId: string): Observable<void> {
    const url = `${this.backend.deviceApiUrl}/devices/${encodeURIComponent(hardwareId)}/unassign`;

    if (LOG_DEBUG) console.log('[DeviceApiService] unassignDevice request:', { hardwareId, url });

    return this.httpClient.post(url, null).pipe(
      map(() => {
        if (LOG_DEBUG) console.log('[DeviceApiService] unassignDevice succeeded:', { hardwareId });
        return;
      }),
      catchError((error: HttpErrorResponse) => {
        const message = this.mapUnassignError(error).message;
        console.error('[DeviceApiService] unassignDevice failed:', message);
        throw new Error(message);
      })
    );
  }

  /**
   * Maps HTTP error responses from unassign endpoint to RegistrationError enum.
   *
   * Error mapping for unassign:
   * - 401: Validation failed (missing/invalid access token)
   * - 404: Device not found
   * - Other: Network error
   */
  private mapUnassignError(error: HttpErrorResponse): Error {
    let message = 'Unassign failed';
    switch (error.status) {
      case 401:
        message = 'Validation failed — check authentication';
        break;
      case 404:
        message = `Device not found (hardwareId: ${error.error?.detail ?? error.message})`;
        break;
      default:
        message = 'Network error — unable to unassign device';
        break;
    }
    return new Error(message);
  }

  /** Backend sends no documented rate-limit duration field; reads the standard `Retry-After` header, falling back to a fixed default. */
  private resolveRateLimitDurationMs(error: HttpErrorResponse): number {
    const retryAfterSeconds = Number(error.headers?.get('Retry-After'));
    return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : RATE_LIMIT_BACKOFF_MS;
  }

  /**
   * Maps HTTP error responses to RegistrationError enum values.
   *
   * Error mapping:
   * - 401: Validation failed (missing/invalid access token)
   * - 404: Token expired or challenge not found
   * - 409: Device already assigned to another account
   * - 422: Assignment verification failed (invalid signature/certificate)
   * - 429: Rate limit exceeded
   * - Other: Network error
   */
  private mapErrorResponse(error: HttpErrorResponse): RegistrationError {
    switch (error.status) {
      case 401:
        return RegistrationError.VALIDATION_FAILED;
      case 404:
        return RegistrationError.TOKEN_EXPIRED;
      case 409:
        return RegistrationError.DEVICE_ALREADY_CLAIMED;
      case 422:
        return RegistrationError.VALIDATION_FAILED;
      case 429:
        return RegistrationError.RATE_LIMITED;
      default:
        return RegistrationError.NETWORK_ERROR;
    }
  }
}

// Re-exports — kept for backward compatibility. The canonical definitions live in
// './device-registration-error'. Import directly from there for new code.
export { isRegistrationError, RegistrationError } from './device-registration-error';

/** Fixed backoff duration for rate limiting (30 seconds) */
export const RATE_LIMIT_BACKOFF_MS = 30_000;
